import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalContainerComponent } from 'src/app/shared/modal-container/modal-container.component';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  NgbDateParserFormatter,
  NgbDatepickerModule,
} from '@ng-bootstrap/ng-bootstrap';
import { CustomDateParserFormatter } from 'src/app/core/providers/date/CustomDateParserFormatter';
import {
  ChangeAssigneeTaskQueryParams,
  TaskFormPlanningDto,
} from 'src/app/core/providers/planning/planning.models';
import { CommonModule } from '@angular/common';
import { SharedModule } from 'src/app/shared/shared.module';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApiService } from 'src/app/core/providers/api/api.service';
import {
  datePickerToDateString,
  formatDateStringToDatePicker,
} from 'src/app/core/helpers/date.helpers';
import { TaskTimeDisplayService } from 'src/app/core/providers/task-time-display/task-time-display.service';

@Component({
  selector: 'app-task-assignee-modal',
  standalone: true,
  imports: [
    ModalContainerComponent,
    ReactiveFormsModule,
    NgbDatepickerModule,
    CommonModule,
    SharedModule,
    TranslateModule,
  ],
  templateUrl: './task-assignee-modal.component.html',
  styleUrl: './task-assignee-modal.component.scss',
  providers: [
    { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
  ],
})
export class TaskAssigneeModalComponent implements OnInit {
  @Input() task!: TaskFormPlanningDto;
  @Input() duration?: number; // Durée du PlanningTask (si différente de task.initialDuration)
  @Input() contextTimeType?: 'PLANNED' | 'ACTUAL' | 'PARTIAL' | string; // Permet d'adapter le libellé selon l'origine
  @Output() refreshWeeklyPlanning = new EventEmitter<any>();
  
  assigneeForm: FormGroup;
  submitted: boolean = false;
  isSaving = false;

  constructor(
    public activeModal: NgbActiveModal,
    public translate: TranslateService,
    private formBuilder: FormBuilder,
    private apiService: ApiService,
    private taskTimeDisplay: TaskTimeDisplayService
  ) {
    this.assigneeForm = this.formBuilder.group({
      executor: ['', [Validators.required]],
      plannedDate: ['', [Validators.required]],
      duration: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.initForm();
  }

  initForm(): void {
    // Récupérer l'ancien assigné par défaut
    const currentExecutor = this.getTaskExecutor();
    
    // Utiliser la durée passée en Input si disponible, sinon task.initialDuration
    const durationToUse = this.duration !== undefined ? this.duration : this.task.initialDuration;
    
    this.assigneeForm.patchValue({
      executor: currentExecutor,
      plannedDate: this.task.plannedDate
        ? formatDateStringToDatePicker(this.task.plannedDate)
        : null,
      duration: durationToUse !== undefined && durationToUse !== null
        ? this.taskTimeDisplay.formatInput(durationToUse)
        : this.taskTimeDisplay.formatInput(0),
    });
  }

  getDateLabel(): string {
    const statusIsDone = this.task?.status?.code === 'DONE';
    const isRealizationTimeType = this.contextTimeType === 'ACTUAL' || this.contextTimeType === 'PARTIAL';
    return statusIsDone || isRealizationTimeType ? 'Date de réalisation' : 'Date de planification';
  }

  getTaskExecutor(): any | null {
    // Récupère l'exécuteur de la tâche
    if (this.task.actors && this.task.actors.length > 0) {
      const executor = this.task.actors.find(
        (actor: any) => actor.role.code === 'EXECUTOR'
      );
      if (executor) {
        return executor.user['@id'];
      }
    }
    return null;
  }

  updateAssignee(): void {
    this.submitted = true;
    
    if (this.assigneeForm.valid) {
      this.isSaving = true;
      
      const formValue = this.assigneeForm.getRawValue();
      console.log('formValue', formValue);
      const userUrl: string = formValue.executor?.user || formValue.executor;
      const queryParams: ChangeAssigneeTaskQueryParams = {
        user: userUrl,
        role: 'EXECUTOR',
        plannedDate: datePickerToDateString(formValue.plannedDate),
        duration: this.taskTimeDisplay.parseInput(formValue.duration),
      };

      // D'abord, mettre à jour la tâche avec la durée (la date est gérée par change_assignee)
      const taskUpdateParams = {
        id: this.task.id,
        initialDuration: this.taskTimeDisplay.parseInput(formValue.duration)
      };
      
      this.apiService.update('tasks', taskUpdateParams).subscribe({
        next: (taskResponse) => {
          // Ensuite, changer l'assignation (qui gère la date)
          this.apiService
            .create(`tasks/${this.task.id}/change_assignee`, queryParams)
            .subscribe({
              next: (response) => {
                this.isSaving = false;
                
                // Utiliser directement les données du formulaire pour mettre à jour la tâche
                const updatedTask = { ...this.task };
                
                // Mettre à jour la date de réalisation
                updatedTask.plannedDate = datePickerToDateString(formValue.plannedDate);
                
                // Mettre à jour la durée initiale
                updatedTask.initialDuration = this.taskTimeDisplay.parseInput(formValue.duration);
            
            // Mettre à jour l'acteur (exécuteur) avec les données du formulaire
            if (updatedTask.actors && updatedTask.actors.length > 0) {
              const executorIndex = updatedTask.actors.findIndex(
                (actor: any) => actor.role.code === 'EXECUTOR'
              );
              if (executorIndex !== -1) {
                const userData = formValue.executor;
                if (userData) {
                  // Si userData est une chaîne (URL), cela signifie que l'utilisateur n'a pas changé
                  // Dans ce cas, on conserve les données originales de l'utilisateur
                  if (typeof userData === 'string') {
                    // Ne rien faire, les données de l'utilisateur restent inchangées
                    // L'acteur garde ses données complètes (firstName, lastName, etc.)
                  } else {
                    // Le dropdown avec returnFullItem="true" retourne l'objet complet
                    // userData.user contient l'URL de l'utilisateur
                    // userData contient les données complètes de l'utilisateur
                    updatedTask.actors[executorIndex].user = {
                      '@id': userData.user || userData['@id'],
                      id: userData.userId || userData.id || '',
                      firstName: userData.firstName || '',
                      lastName: userData.lastName || ''
                    };
                  }
                }
              }
            }

                this.refreshWeeklyPlanning.emit({
                  action: 'changeAssignee',
                  taskId: this.task.id,
                  updatedTask: updatedTask,
                });
                this.closeModal();
              },
              error: (error) => {
                this.isSaving = false;
                // Ne pas fermer la modale en cas d'erreur
                // this.closeModal(); // Commenté pour garder la modale ouverte
              },
            });
        },
        error: (error) => {
          this.isSaving = false;
          // Ne pas fermer la modale en cas d'erreur
          // this.closeModal(); // Commenté pour garder la modale ouverte
        }
      });
    }
  }

  closeModal(): void {
    this.activeModal.close();
  }
}
